import { ActivityName, TDrugSlot } from "app/_modules/core/types";
import { QuestionnaireResult } from "app/_modules/core/questionnaire";
import { TMeasurementAlert } from "app/types/alert";
import { TActivityStatus } from "app/_modules/core/constants";

export enum ActivityType {
  PATHWAY_VITAL_PARAMETER = 'pathwayvitalparameter',
  PATHWAY_DRUG = 'pathwaydrug',
  PATHWAY_QUESTIONNAIRE = 'pathwayquestionnaire',
  PATHWAY_EXAM = 'pathwayexam',
  PATHWAY_APPOINTMENT = 'appointment',
  PATHWAY_TELEASSISTANCE_REQUEST = 'teleassistancerequest',
}

export type TeleassistancePreferredDate = 'asap' | 'next_few_days' | 'next_week' | 'indifferent';
export type TeleassistancePreferredTime = 'morning' | 'afternoon' | 'indifferent';
export type TeleassistanceAppointmentStatus = 'to_be_scheduled' | 'scheduled' | 'cancelled';

export interface TExam {
  id: string;
  label: string;
}

export interface TTeleassistanceAppointment {
  id: string;
  date: string;
  time: string;
  roomUrl: string;
  doctor: {
    id: string;
    firstName: string;
    lastName: string;
    title: string;
  };
}

export interface TTeleassistanceRequest {
  id: string;
  status: TeleassistanceAppointmentStatus;
  source: 'patient';
  preferredDate: TeleassistancePreferredDate;
  preferredTime: TeleassistancePreferredTime;
  additionalDetails: string;
  exam: TExam;
  createdAt: string;
  cancelledAt: string | null;
  canCancel: boolean;
  appointment: TTeleassistanceAppointment | null;
}

export type TActivityType = typeof ActivityType[keyof typeof ActivityType];

export interface IPathwayActivity {
  activityId: string;
  activityType: TActivityType;
  activityName: string;
  status: TActivityStatus;
  completedAt: string | null;
  standalone: boolean;
  isFlexible?: boolean;
  unlockAt?: string | null;
  unlocksAfterActivityId?: string | null;
  measurement?: Record<string, any>;
  drugAdministered?: boolean;
  drugIntakeStatus?: 'to_take' | 'in_slot' | 'out_of_slot' | 'not_taken' | 'not_registered';
  actualSlot?: TDrugSlot | null;
  questionnaireId?: string;
  questionnaireResult?: QuestionnaireResult;
  questionnaireResultId?: string;
  teleassistanceRequest?: TTeleassistanceRequest;
  pendingAlert?: {
    measId: string;
    vitPName: string;
    isRemeasurement: boolean;
  } | null;
  appointment?: {
    id: string;
    date: string;
    time: string;
    reason?: string;
    modality?: string;
    additionalDetails?: string;
    status: string;
    professional?: {
      id?: string;
      userId?: string;
      firstName?: string;
      lastName?: string;
      specialization?: string;
      role?: string;
      title?: string;
    };
    exam?: {
      id: string;
      name: string;
      label: string;
    };
  };
}

export interface IPathwayActivitiesResponse {
  date: string;
  daily: IPathwayActivity[];
  morning: IPathwayActivity[];
  afternoon: IPathwayActivity[];
  evening: IPathwayActivity[];
}

export interface IPathwayMeasurementRequest {
  telemonitoringPathwayId: string;
  type: ActivityName;
  timestamp: string;
  measurement: Record<string, number>;
}

export type MeasurementFields = {
  [ActivityName.HEART_RATE]: { heart_rate: number };
  [ActivityName.WEIGHT]: { weight: number };
  [ActivityName.TEMPERATURE]: { temperature: number };
  [ActivityName.BLOOD_PRESSURE]: { systolic: number; diastolic: number };
  [ActivityName.SPO2]: { saturation: number };
  [ActivityName.STEP]: { step: number };
  [ActivityName.DYSPNEA]: { dyspnea: number };
  [ActivityName.SPIROMETRY]: { pef: number; fet?: number };
};

export interface IMeasurementResponse<T extends keyof MeasurementFields = keyof MeasurementFields> {
  type: string;
  measurement: MeasurementFields[T];
  timestamp: string;
  telemonitoring_pathway_id: string;
  alert?: TMeasurementAlert | null;
}

export type TMeasurementResponse = {
  [K in keyof MeasurementFields]: IMeasurementResponse<K>;
}[keyof MeasurementFields];

export type TDataFreshness = {
  lastMeasuredAt: string;
  lastGatewayConnectionAt: string;
  isGatewayConnected: boolean;
};

export const DEVICE_TUTORIAL_KEY = {
  app: 'app',
  tablet: 'tablet',
  gateway: 'gateway',
  wifi: 'wifi',
  bloodPressure: 'blood_pressure',
  spo2: 'spo2',
  weight: 'weight',
  temperature: 'temperature',
  ecg: 'ecg',
  spirometry: 'spirometry',
  step: 'step',
  heartRate: 'heart_rate',
} as const;

export type TDeviceTutorialKey = typeof DEVICE_TUTORIAL_KEY[keyof typeof DEVICE_TUTORIAL_KEY];

export type TDeviceTutorial = {
  deviceKey: TDeviceTutorialKey;
  mediaUrl: string;
};

export type TDeviceTutorialsResponse = {
  tutorials: TDeviceTutorial[];
};

export type TComplianceTrendDirection = 'up' | 'down' | 'stable';

export type TDailyComplianceScore = {
  date: string;
  score: number;
};

export type TComplianceScores = {
  overall: number;
  trendDirection: TComplianceTrendDirection;
  dailyScores: TDailyComplianceScore[];
};
